interface ActiveSound {
  source: AudioBufferSourceNode;
  gainNode: GainNode;
  startTime: number;
  priority: number;
  category?: 'gold' | 'tower_attack'; // Track sound category for ducking
  name: string;
}

export class AudioSystem {
  private audioContext: AudioContext | null = null;
  private masterVolume: number = 1.0;
  private musicVolume: number = 0.5;
  private sfxVolume: number = 0.7;
  private musicSource: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private currentMusicSpeed: number = 1.0;
  private isMusicPlaying: boolean = false;
  
  // Sound limiting system
  private sfxGain: GainNode | null = null; // Master SFX gain node to prevent clipping
  private activeSounds: ActiveSound[] = [];
  private readonly MAX_CONCURRENT_SFX = 12; // Maximum concurrent sound effects
  private readonly MAX_LOW_PRIORITY_SFX = 7; // Maximum low priority sounds at once
  private readonly VOLUME_DUCK_THRESHOLD = 8; // Start ducking volume when this many sounds play
  private readonly VOLUME_DUCK_AMOUNT = 0.15; // Reduce volume by this amount when ducking
  
  // Category-specific ducking (lower volume by 30% when multiple of same category play)
  private activeSoundsByCategory: Map<string, number> = new Map();
  private readonly CATEGORY_DUCK_AMOUNT = 0.30; // 30% volume reduction
  
  // Audio file cache
  private audioBufferCache: Map<string, AudioBuffer> = new Map();
  private loadingPromises: Map<string, Promise<AudioBuffer | null>> = new Map();
  
  // Sound name to file path mapping
  private readonly soundFileMap: Record<string, string | string[]> = {
    'tower_arrow_fire': '/audio/SFX/18._Arrow_Tower_Shot.wav',
    'tower_cannon_fire': '/audio/SFX/19._Cannon_Tower_Shot.wav',
    'tower_sniper_fire': '/audio/SFX/20._Sniper_Tower_Shot.wav',
    'wave_complete': '/audio/SFX/16 Wave Complete.wav',
    'wave_start': '/audio/SFX/15.game start (after clicking instructions at the start).wav',
    'menu_single_player': '/audio/SFX/15._Wave Start.wav',
    'button_click': '/audio/SFX/1 UI_Click_1.wav',
    'ui_hover': '/audio/SFX/2._UI_Hover.wav',
    'ui_confirm': '/audio/SFX/3. UI_Confirm_OK.wav',
    'ui_cancel': '/audio/SFX/4. UI_Cancel_Back.wav',
    'tower_place': '/audio/SFX/8. Building Placed.wav',
    'wall_place': '/audio/SFX/8. Building Placed.wav',
    'tower_upgrade': '/audio/SFX/Upgrade_Purchase_A.wav',
    'tower_upgrade_A': '/audio/SFX/Upgrade_Purchase_A.wav',
    'tower_upgrade_B': '/audio/SFX/Upgrade_Purchase_B.wav',
    'gold_gain': ['/audio/SFX/Gold_Earned_1.wav', '/audio/SFX/Gold_Earned_2.wav'],
    'gold_gain_1': '/audio/SFX/Gold_Earned_1.wav',
    'gold_gain_2': '/audio/SFX/Gold_Earned_2.wav',
  };

  constructor() {
    try {
      const AudioContextClass = AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass();
        // Create master SFX gain node to control overall SFX volume and prevent clipping
        this.sfxGain = this.audioContext.createGain();
        this.sfxGain.gain.value = 1.0; // Start at full volume
        this.sfxGain.connect(this.audioContext.destination);
      }
    } catch (e) {
      console.warn('AudioContext not available:', e);
    }
  }

  /**
   * Initialize audio context (required for browser autoplay policies)
   */
  async initialize(): Promise<void> {
    if (!this.audioContext) return;
    
    // Resume audio context if suspended (required for user interaction)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Get priority for a sound (higher = more important, won't be stopped as easily)
   */
  private getSoundPriority(name: string): number {
    // High priority sounds (important game events)
    if (name === 'enemy_reach_goal' || name === 'wave_complete' || name === 'wave_start' || 
        name === 'tower_upgrade' || name === 'tower_upgrade_A' || name === 'tower_upgrade_B' || 
        name === 'sniper_execute' || name === 'menu_single_player') {
      return 3;
    }
    // Medium priority sounds (user actions and special combat events)
    if (name === 'tower_place' || name === 'tower_sell' || name === 'wall_place' || 
        name === 'button_click' || name === 'ui_hover' || name === 'ui_confirm' || name === 'ui_cancel' ||
        name === 'invalid_placement' || name === 'crit_hit' || 
        name === 'attack_miss' || name === 'building_destroyed') {
      return 2;
    }
    // Low priority (combat sounds - can be skipped if too many sounds)
    // tower_arrow_fire, tower_cannon_fire, tower_sniper_fire, projectile_hit, enemy_death, gold_gain, enemy_spawn
    return 1;
  }
  
  /**
   * Get category for a sound (for category-specific volume ducking)
   */
  private getSoundCategory(name: string): 'gold' | 'tower_attack' | undefined {
    if (name.includes('gold_gain')) {
      return 'gold';
    }
    if (name.includes('tower_') && name.includes('fire')) {
      return 'tower_attack';
    }
    return undefined;
  }
  
  /**
   * Calculate category-specific volume ducking
   * Returns volume multiplier (1.0 = no ducking, 0.7 = 30% reduction)
   */
  private calculateCategoryDucking(category?: 'gold' | 'tower_attack'): number {
    if (!category) return 1.0; // No category, no ducking
    
    const count = this.activeSoundsByCategory.get(category) || 0;
    if (count > 1) {
      // Multiple sounds of same category playing - duck by 30%
      return 1.0 - this.CATEGORY_DUCK_AMOUNT;
    }
    return 1.0; // Only one sound, no ducking
  }

  /**
   * Clean up finished sounds from active list
   */
  private cleanupFinishedSounds(): void {
    const now = Date.now();
    
    // Clear category counts
    this.activeSoundsByCategory.clear();
    
    this.activeSounds = this.activeSounds.filter(sound => {
      // Check if sound is still playing (rough estimate based on duration)
      // Most sounds are ~0.1s, so check if started more than 0.2s ago
      const elapsed = (now - sound.startTime) / 1000;
      if (elapsed > 0.2) {
        // Sound likely finished, disconnect and remove
        try {
          sound.source.disconnect();
          sound.gainNode.disconnect();
        } catch (e) {
          // Already disconnected
        }
        return false;
      }
      
      // Track active sounds by category
      if (sound.category) {
        const count = this.activeSoundsByCategory.get(sound.category) || 0;
        this.activeSoundsByCategory.set(sound.category, count + 1);
      }
      
      return true;
    });
  }

  /**
   * Remove oldest/lowest priority sound if at limit
   */
  private makeRoomForSound(priority: number): void {
    // Count low priority sounds
    const lowPriorityCount = this.activeSounds.filter(s => s.priority === 1).length;
    
    // If this is a low priority sound and we're at the limit, skip it
    if (priority === 1 && lowPriorityCount >= this.MAX_LOW_PRIORITY_SFX) {
      return; // Don't play this low priority sound
    }
    
    // If we're at max concurrent sounds, need to make room
    if (this.activeSounds.length >= this.MAX_CONCURRENT_SFX) {
      // Find lowest priority sound (or oldest if same priority)
      // Never remove high/medium priority sounds to make room for low priority
      let toRemove: ActiveSound | null = null;
      let toRemoveIndex = -1;

      for (let i = 0; i < this.activeSounds.length; i++) {
        const sound = this.activeSounds[i];
        // Only consider removing sounds with priority <= new sound's priority
        // Never remove high/medium priority to make room for low priority
        if (sound.priority <= priority) {
          if (!toRemove || 
              sound.priority < toRemove.priority || 
              (sound.priority === toRemove.priority && sound.startTime < toRemove.startTime)) {
            toRemove = sound;
            toRemoveIndex = i;
          }
        }
      }

      // Only remove if we found a sound to remove and new sound has higher or equal priority
      if (toRemove && priority >= toRemove.priority) {
        try {
          toRemove.source.stop();
          toRemove.source.disconnect();
          toRemove.gainNode.disconnect();
        } catch (e) {
          // Already stopped
        }
        this.activeSounds.splice(toRemoveIndex, 1);
      } else if (priority === 1) {
        // Low priority sound and can't make room - skip it
        return;
      }
    }
  }

  /**
   * Calculate volume ducking based on active sound count
   */
  private calculateVolumeDucking(): number {
    const activeCount = this.activeSounds.length;
    if (activeCount <= this.VOLUME_DUCK_THRESHOLD) {
      return 1.0; // No ducking
    }
    
    // Calculate ducking: more sounds = more ducking
    const excessSounds = activeCount - this.VOLUME_DUCK_THRESHOLD;
    const duckAmount = Math.min(0.5, excessSounds * this.VOLUME_DUCK_AMOUNT); // Max 50% reduction
    return 1.0 - duckAmount;
  }

  /**
   * Load an audio file and cache it
   */
  private async loadAudioFile(filePath: string): Promise<AudioBuffer | null> {
    if (!this.audioContext) return null;
    
    // Check cache first
    if (this.audioBufferCache.has(filePath)) {
      return this.audioBufferCache.get(filePath)!;
    }
    
    // Check if already loading
    if (this.loadingPromises.has(filePath)) {
      return this.loadingPromises.get(filePath)!;
    }
    
    // Start loading
    const loadPromise = (async () => {
      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          console.warn(`Failed to load audio file: ${filePath} (${response.statusText})`);
          return null;
        }
        
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);
        
        // Cache the buffer
        this.audioBufferCache.set(filePath, audioBuffer);
        return audioBuffer;
      } catch (e) {
        console.warn(`Error loading audio file: ${filePath}`, e);
        return null;
      } finally {
        // Remove from loading promises
        this.loadingPromises.delete(filePath);
      }
    })();
    
    this.loadingPromises.set(filePath, loadPromise);
    return loadPromise;
  }

  /**
   * Play a sound effect (loads from file if available, falls back to synthesized)
   * Can be called without await - will load and play asynchronously
   * @param pitch - Playback rate multiplier (1.0 = normal, 1.5 = 50% faster/higher pitch)
   */
  playSound(name: string, volume: number = 1.0, pitch: number = 1.0): void {
    if (!this.audioContext || this.audioContext.state === 'closed' || !this.sfxGain) return;

    // Clean up finished sounds first
    this.cleanupFinishedSounds();

    const priority = this.getSoundPriority(name);
    
    // Make room if needed
    this.makeRoomForSound(priority);

    // Skip if still at limit and this sound has lower priority
    // Also check low priority limit
    const lowPriorityCount = this.activeSounds.filter(s => s.priority === 1).length;
    if (priority === 1 && lowPriorityCount >= this.MAX_LOW_PRIORITY_SFX) {
      return; // Skip low priority sound if at limit
    }
    
    if (this.activeSounds.length >= this.MAX_CONCURRENT_SFX) {
      const lowestPriority = Math.min(...this.activeSounds.map(s => s.priority));
      if (priority < lowestPriority) {
        return; // Skip this sound
      }
    }

    const baseVolume = this.masterVolume * this.sfxVolume * volume;
    if (baseVolume <= 0) return;

    // Get sound category for ducking
    const category = this.getSoundCategory(name);

    // Apply volume ducking when many sounds are active
    const duckMultiplier = this.calculateVolumeDucking();
    // Apply category-specific ducking (30% reduction when multiple of same type)
    const categoryDuckMultiplier = this.calculateCategoryDucking(category);
    const finalVolume = baseVolume * duckMultiplier * categoryDuckMultiplier;

    // Try to load and play audio file asynchronously (fire and forget)
    const filePathOrArray = this.soundFileMap[name];
    if (filePathOrArray) {
      // Handle arrays (for random selection, e.g., gold sounds)
      let filePath: string;
      if (Array.isArray(filePathOrArray)) {
        // Randomly select from array
        filePath = filePathOrArray[Math.floor(Math.random() * filePathOrArray.length)];
      } else {
        filePath = filePathOrArray;
      }
      
      // Load and play asynchronously
      this.loadAudioFile(filePath).then(audioBuffer => {
        if (audioBuffer && this.audioContext && this.sfxGain) {
          try {
            const source = this.audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.playbackRate.value = pitch; // Apply pitch modification
            
            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = finalVolume;
            
            // Connect through master SFX gain node to prevent clipping
            source.connect(gainNode);
            gainNode.connect(this.sfxGain);
            source.start(0);

            // Track active sound with category
            this.activeSounds.push({
              source,
              gainNode,
              startTime: Date.now(),
              priority,
              category,
              name,
            });
            
            // Update category count
            if (category) {
              const count = this.activeSoundsByCategory.get(category) || 0;
              this.activeSoundsByCategory.set(category, count + 1);
            }

            // Auto-remove when sound ends
            source.onended = () => {
              const index = this.activeSounds.findIndex(s => s.source === source);
              if (index >= 0) {
                this.activeSounds.splice(index, 1);
              }
            };
          } catch (e) {
            console.warn('Failed to play loaded audio:', name, e);
            // Fallback to synthesized sound on error
            this.playSynthesizedSound(name, finalVolume, priority, pitch);
          }
        } else {
          // File not loaded, fallback to synthesized sound
          this.playSynthesizedSound(name, finalVolume, priority, pitch);
        }
      }).catch(e => {
        console.warn('Failed to load audio file:', name, e);
        // Fallback to synthesized sound on error
        this.playSynthesizedSound(name, finalVolume, priority, pitch);
      });
      
      return; // Don't play synthesized sound immediately - wait for file load
    }
    
    // No file mapping, use synthesized sound
    this.playSynthesizedSound(name, finalVolume, priority, pitch);
  }

  /**
   * Play a synthesized sound effect (fallback)
   */
  private playSynthesizedSound(name: string, finalVolume: number, priority: number, pitch: number = 1.0, category?: 'gold' | 'tower_attack'): void {
    if (!this.audioContext || !this.sfxGain) return;

    try {
      // Generate simple synthesized sounds based on name
      const duration = 0.1;
      const sampleRate = this.audioContext.sampleRate;
      const buffer = this.audioContext.createBuffer(1, duration * sampleRate, sampleRate);
      const data = buffer.getChannelData(0);

      let frequency = 440; // Default A4
      let waveType: 'sine' | 'square' | 'sawtooth' = 'sine';

      // Sound type mapping
      switch (name) {
        case 'tower_arrow_fire':
          frequency = 600;
          waveType = 'sine';
          break;
        case 'tower_cannon_fire':
          frequency = 200;
          waveType = 'square';
          break;
        case 'tower_sniper_fire':
          frequency = 800;
          waveType = 'sine';
          break;
        case 'projectile_hit':
          frequency = 300;
          waveType = 'square';
          break;
        case 'enemy_death':
          frequency = 150;
          waveType = 'sawtooth';
          break;
        case 'building_destroyed':
          frequency = 120;
          waveType = 'square';
          break;
        case 'tower_upgrade':
          frequency = 500;
          waveType = 'sine';
          break;
        case 'wall_place':
          frequency = 400;
          waveType = 'square';
          break;
        case 'tower_place':
          frequency = 500;
          waveType = 'sine';
          break;
        case 'tower_sell':
          frequency = 250;
          waveType = 'sawtooth';
          break;
        case 'invalid_placement':
          frequency = 200;
          waveType = 'square';
          break;
        case 'button_click':
          frequency = 800;
          waveType = 'sine';
          break;
        case 'wave_start':
          frequency = 600;
          waveType = 'sine';
          break;
        case 'wave_complete':
          frequency = 700;
          waveType = 'sine';
          break;
        case 'gold_gain':
          frequency = 900;
          waveType = 'sine';
          break;
        case 'enemy_spawn':
          frequency = 350;
          waveType = 'square';
          break;
        case 'enemy_reach_goal':
          frequency = 100;
          waveType = 'sawtooth';
          break;
        case 'crit_hit':
          frequency = 1200;
          waveType = 'sine';
          break;
        case 'attack_miss':
          frequency = 300;
          waveType = 'sawtooth';
          break;
        case 'sniper_execute':
          frequency = 1500;
          waveType = 'sine';
          break;
        default:
          frequency = 440;
      }

      // Generate waveform
      for (let i = 0; i < data.length; i++) {
        const t = i / sampleRate;
        let value = 0;
        
        switch (waveType) {
          case 'sine':
            value = Math.sin(2 * Math.PI * frequency * t);
            break;
          case 'square':
            value = Math.sin(2 * Math.PI * frequency * t) > 0 ? 1 : -1;
            break;
          case 'sawtooth':
            value = 2 * (t * frequency - Math.floor(t * frequency + 0.5));
            break;
        }
        
        // Apply envelope (fade out)
        const envelope = 1 - (t / duration);
        data[i] = value * envelope * finalVolume;
      }

      // Play the sound
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = pitch; // Apply pitch modification
      
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = finalVolume;
      
      // Connect through master SFX gain node to prevent clipping
      source.connect(gainNode);
      gainNode.connect(this.sfxGain);
      source.start(0);

      // Track active sound with category
      this.activeSounds.push({
        source,
        gainNode,
        startTime: Date.now(),
        priority,
        category,
        name,
      });
      
      // Update category count
      if (category) {
        const count = this.activeSoundsByCategory.get(category) || 0;
        this.activeSoundsByCategory.set(category, count + 1);
      }

      // Auto-remove when sound ends
      source.onended = () => {
        const index = this.activeSounds.findIndex(s => s.source === source);
        if (index >= 0) {
          this.activeSounds.splice(index, 1);
        }
      };
    } catch (e) {
      console.warn('Failed to play synthesized sound:', name, e);
    }
  }

  /**
   * Start background music loop
   */
  async startMusic(speed: number = 1.0): Promise<void> {
    if (!this.audioContext || this.audioContext.state === 'closed') return;
    if (this.isMusicPlaying) {
      this.setMusicSpeed(speed);
      return;
    }

    try {
      // Load the actual music file
      const musicUrl = '/audio/Mazing Hunters Main Theme.mp3';
      const response = await fetch(musicUrl);
      if (!response.ok) {
        throw new Error(`Failed to load music: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      
      this.playMusicLoop(audioBuffer, speed);
      this.isMusicPlaying = true;
    } catch (e) {
      console.warn('Failed to start music:', e);
      // Fallback to synthesized music if file loading fails
      try {
        const duration = 4.0; // 4 second loop
        const sampleRate = this.audioContext.sampleRate;
        const buffer = this.audioContext.createBuffer(2, duration * sampleRate, sampleRate);
        const leftChannel = buffer.getChannelData(0);
        const rightChannel = buffer.getChannelData(1);

        // Create a simple melody pattern
        const notes = [440, 523.25, 659.25, 783.99, 880]; // A, C, E, G, A
        const noteDuration = duration / notes.length;

        for (let i = 0; i < leftChannel.length; i++) {
          const t = i / sampleRate;
          const noteTime = t % noteDuration;
          const currentNote = notes[Math.floor(t / noteDuration) % notes.length];
          
          // Generate a pleasant tone with harmonics
          const fundamental = Math.sin(2 * Math.PI * currentNote * t);
          const harmonic2 = Math.sin(2 * Math.PI * currentNote * 2 * t) * 0.3;
          const harmonic3 = Math.sin(2 * Math.PI * currentNote * 3 * t) * 0.1;
          
          // Apply envelope per note
          const noteEnvelope = Math.min(1, noteTime * 10) * (1 - Math.min(1, (noteDuration - noteTime) * 2));
          const value = (fundamental + harmonic2 + harmonic3) * noteEnvelope * this.masterVolume * this.musicVolume * 0.3;
          
          leftChannel[i] = value;
          rightChannel[i] = value;
        }

        this.playMusicLoop(buffer, speed);
        this.isMusicPlaying = true;
      } catch (fallbackError) {
        console.warn('Failed to start fallback music:', fallbackError);
      }
    }
  }

  /**
   * Play music in a loop
   */
  private playMusicLoop(buffer: AudioBuffer, speed: number): void {
    if (!this.audioContext) return;

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.playbackRate.value = speed;
    this.currentMusicSpeed = speed;

    this.musicGain = this.audioContext.createGain();
    this.musicGain.gain.value = this.masterVolume * this.musicVolume * 0.3;

    source.connect(this.musicGain);
    this.musicGain.connect(this.audioContext.destination);
    source.start(0);

    this.musicSource = source;

    // Handle loop end (shouldn't happen with loop=true, but just in case)
    source.onended = () => {
      if (this.isMusicPlaying) {
        this.playMusicLoop(buffer, speed);
      }
    };
  }

  /**
   * Set music playback speed (affects both speed and pitch)
   */
  setMusicSpeed(speed: number): void {
    if (!this.musicSource || !this.isMusicPlaying) return;
    
    // Calculate speed multiplier: 1x = 1.0, 2x = 1.25 (25% faster), 3x = 1.5 (50% faster)
    let playbackRate = 1.0;
    if (speed === 2.0) {
      playbackRate = 1.25; // 25% faster
    } else if (speed === 3.0) {
      playbackRate = 1.5; // 50% faster (or 25% faster than 2x)
    }
    
    this.musicSource.playbackRate.value = playbackRate;
    this.currentMusicSpeed = speed;
  }

  /**
   * Pause music (can be resumed)
   */
  pauseMusic(): void {
    if (this.musicSource && this.audioContext) {
      this.audioContext.suspend();
    }
  }

  /**
   * Resume music
   */
  resumeMusic(): void {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  /**
   * Stop music
   */
  stopMusic(): void {
    if (this.musicSource) {
      try {
        this.musicSource.stop();
      } catch (e) {
        // Already stopped
      }
      this.musicSource = null;
      this.musicGain = null;
      this.isMusicPlaying = false;
    }
  }

  /**
   * Set master volume (0.0 to 1.0)
   */
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.musicGain) {
      this.musicGain.gain.value = this.masterVolume * this.musicVolume * 0.3;
    }
  }

  /**
   * Set music volume (0.0 to 1.0)
   */
  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    if (this.musicGain) {
      this.musicGain.gain.value = this.masterVolume * this.musicVolume * 0.3;
    }
  }

  /**
   * Set sound effects volume (0.0 to 1.0)
   */
  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    // Update master SFX gain node to prevent clipping
    if (this.sfxGain) {
      // Keep master gain at safe level to prevent distortion
      this.sfxGain.gain.value = Math.min(0.8, volume * 1.2); // Cap at 80% to prevent clipping
    }
  }

  /**
   * Get current music speed
   */
  getMusicSpeed(): number {
    return this.currentMusicSpeed;
  }

  /**
   * Get audio context (for custom sound generation)
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }
}
